/**
 * Beneficios del Club por nivel y por país.
 *
 * Esto NO es dato de ejemplo: son las reglas del Club provistas por Puppis, y
 * por eso vive en su propio archivo y no en datos-demo.ts. Son distintas en
 * Argentina y en Colombia —cosa que el prototipo ya advertía— así que se
 * indexan por país.
 *
 * Están hardcodeadas porque ninguna API las expone hoy; son reglas de negocio.
 * Si el día de mañana se publican desde el backend, este archivo es el único
 * lugar a reemplazar: tanto la pantalla Mis beneficios como el resumen de la
 * home leen de acá.
 *
 * ⚠️ Los porcentajes y condiciones los define Puppis / Legales. Si cambian,
 * se editan acá. Última actualización con los valores pasados por Guillermo.
 */

import { mismoNivel } from './niveles';

export interface Beneficio {
  /** El número o palabra que va destacado: "5%", "Gratis". Puede faltar. */
  destacado? : string;
  /** La descripción del beneficio. */
  texto : string;
}

export interface NivelBeneficios {
  nombre : string;
  beneficios : Beneficio[];
}

/**
 * General (nivel base) no tiene beneficios de descuento asignados: es una
 * decisión de negocio, no un dato faltante. La pantalla lo muestra con el
 * camino a Aliado en vez de una lista vacía.
 */
const AR : NivelBeneficios[] = [
  {
    nombre: 'Aliado',
    beneficios: [
      { destacado: '5%',  texto: 'OFF en compras (sin tope)' },
      { destacado: '10%', texto: 'OFF en Nufood' },
      { destacado: '10%', texto: 'OFF en marcas recomendadas' },
      { destacado: '10%', texto: 'OFF en snacks' },
      { destacado: '10%', texto: 'OFF en piedras Puppis' },
      { texto: 'Eventos y actividades exclusivas' }
    ]
  },
  {
    nombre: 'Amigo Fiel',
    beneficios: [
      { destacado: '7%',  texto: 'OFF en compras (sin tope)' },
      { destacado: '10%', texto: 'OFF en Nufood' },
      { destacado: '10%', texto: 'OFF en marcas recomendadas' },
      { destacado: '15%', texto: 'OFF en snacks' },
      { destacado: '15%', texto: 'OFF en piedras Puppis' },
      { texto: 'Eventos y actividades exclusivas' }
    ]
  },
  {
    nombre: 'Fanático',
    beneficios: [
      { destacado: '10%', texto: 'OFF en compras (sin tope)' },
      { destacado: '15%', texto: 'OFF en Nufood' },
      { destacado: '15%', texto: 'OFF en marcas recomendadas' },
      { destacado: '20%', texto: 'OFF en snacks' },
      { destacado: '20%', texto: 'OFF en piedras Puppis' },
      { texto: 'Eventos y actividades exclusivas' }
    ]
  }
];

const CO : NivelBeneficios[] = [
  {
    nombre: 'Aliado',
    beneficios: [
      { destacado: '3,5%', texto: 'OFF en Alimentos y Farmacia' },
      { destacado: '5%',   texto: 'OFF en Productos marca Puppis y Nufood' },
      { destacado: '5%',   texto: 'OFF en Baño y peluquería' },
      { destacado: 'Gratis', texto: 'corte de uñas (según disponibilidad)' },
      { destacado: '10%',  texto: 'OFF en planes de Prevención Plus' },
      { destacado: '15%',  texto: 'OFF en servicios veterinarios de Petplus' },
      { texto: 'Promociones exclusivas' },
      { texto: 'Eventos exclusivos y sorteos' }
    ]
  },
  {
    nombre: 'Amigo Fiel',
    beneficios: [
      { destacado: '7%',   texto: 'OFF en Alimentos y Farmacia' },
      { destacado: '10%',  texto: 'OFF en Productos marca Puppis y Nufood' },
      { destacado: '7%',   texto: 'OFF en Baño y peluquería' },
      { destacado: 'Gratis', texto: 'corte de uñas (según disponibilidad)' },
      { destacado: '15%',  texto: 'OFF en planes de Prevención Plus' },
      { destacado: '15%',  texto: 'OFF en servicios veterinarios de Petplus' },
      { texto: 'Promociones exclusivas' },
      { texto: 'Eventos exclusivos y sorteos' }
    ]
  },
  {
    nombre: 'Fanático',
    beneficios: [
      { destacado: '10%',  texto: 'OFF en Alimentos y Farmacia' },
      { destacado: '13%',  texto: 'OFF en Productos marca Puppis y Nufood' },
      { destacado: '10%',  texto: 'OFF en Baño y peluquería' },
      { destacado: 'Gratis', texto: 'corte de uñas (según disponibilidad)' },
      { destacado: '20%',  texto: 'OFF en planes de Prevención Plus' },
      { destacado: '15%',  texto: 'OFF en servicios veterinarios de Petplus' },
      { texto: 'Promociones exclusivas' },
      { texto: 'Eventos exclusivos y sorteos' }
    ]
  }
];

const POR_PAIS : { [pais : string] : NivelBeneficios[] } = { AR, CO };

/** Todos los niveles con beneficios del país (Aliado en adelante). */
export function beneficiosDePais(countryCode : string | null | undefined) : NivelBeneficios[] {
  return POR_PAIS[(countryCode ?? 'AR').toUpperCase()] ?? AR;
}

/** Los beneficios de un nivel puntual, o [] si es General o no existe. */
export function beneficiosDeNivel(countryCode : string | null | undefined, nivel : string | null | undefined) : Beneficio[] {
  if (!nivel) return [];
  return beneficiosDePais(countryCode).find(n => mismoNivel(n.nombre, nivel))?.beneficios ?? [];
}
